import {
  createFileRoute,
  Outlet,
  redirect,
  useChildMatches,
  useNavigate,
} from '@tanstack/react-router'
import { useMemo } from 'react'
import {
  conditionSearchParam,
  decodeConditions,
  encodeConditions,
} from '@/components/condition-filter'
import { invoiceConditionFields } from '@/components/condition-filter/accounting-fields'
import type {
  InvoicePaymentStatus,
  InvoicePostingStatus,
  InvoiceSort,
  InvoiceStatus,
} from '@/features/accounting-invoices/api'
import type {
  InvoiceGroupBy,
  InvoiceListParams,
} from '@/features/accounting-invoices/invoice-group-types'
import { InvoiceList } from '@/features/accounting-invoices/invoice-list'
import { hasGrant } from '@/lib/me'

/**
 * Route shell for the invoices list.
 *
 * Route definition, guard, render — nothing else, per the route-shell rule in
 * `apps/company/AGENTS.md`. `validateSearch` belongs here because the URL is a
 * route concern; what the screen does with the values is the feature's.
 *
 * The filters live in the search params rather than in a store, so a filtered
 * list is linkable and survives a reload — exactly the kind of view one
 * accountant sends another.
 *
 * The grant guard is presentation only. The API enforces
 * `accounting_invoice.view` on every invoice endpoint, so a stale grant still
 * 403s server-side; this only keeps a user without the grant out of a screen
 * they cannot use.
 *
 * ## Why this renders an Outlet
 *
 * `invoices.new.tsx`, `invoices.$invoiceId.tsx` and `invoices.$invoiceId.edit.tsx`
 * are CHILDREN of this route in flat file routing, so without an Outlet they
 * never mount and their URLs silently render this list instead — the same
 * shape `hr/expenses.tsx` documents.
 */
export const Route = createFileRoute('/_authenticated/accounting/invoices')({
  validateSearch: (search: Record<string, unknown>): InvoiceListParams => ({
    c: conditionSearchParam(search.c),
    match: search.match === 'any' ? 'any' : undefined,
    groupBy: isInvoiceGroupBy(search.groupBy) ? search.groupBy : undefined,
    postingStatus: isInvoicePostingStatus(search.postingStatus) ? search.postingStatus : undefined,
    paymentStatus: isInvoicePaymentStatus(search.paymentStatus) ? search.paymentStatus : undefined,
    status: isInvoiceStatus(search.status) ? search.status : undefined,
    customer: typeof search.customer === 'string' && search.customer ? search.customer : undefined,
    analysisTagIds: stringArray(search.analysisTagIds),
    from: typeof search.from === 'string' ? search.from : undefined,
    to: typeof search.to === 'string' ? search.to : undefined,
    overdue: search.overdue === '1' || search.overdue === true ? true : undefined,
    page: positiveInteger(search.page),
    perPage: invoicePageSize(search.perPage),
    sort: isInvoiceSort(search.sort) ? search.sort : undefined,
    direction:
      search.direction === 'asc' || search.direction === 'desc' ? search.direction : undefined,
  }),
  beforeLoad: ({ context }) => {
    if (!hasGrant(context.queryClient, 'accounting_invoice.view')) {
      throw redirect({ to: '/accounting' })
    }
  },
  component: AccountingInvoicesRoute,
})

function AccountingInvoicesRoute() {
  const navigate = useNavigate()
  const children = useChildMatches()
  const search = Route.useSearch()
  const { conditions, match } = useMemo(
    () => decodeConditions({ c: search.c, match: search.match }, invoiceConditionFields),
    [search.c, search.match],
  )
  const params = { ...search, conditions, match }

  if (children.length > 0) {
    return <Outlet />
  }

  return (
    <InvoiceList
      params={params}
      documentType="invoice"
      onParamsChange={(next) =>
        void navigate({
          to: '/accounting/invoices',
          search: {
            ...next,
            ...encodeConditions(next.conditions ?? [], next.match ?? 'all'),
            conditions: undefined,
          },
        })
      }
    />
  )
}

const POSTING_STATUSES: InvoicePostingStatus[] = ['draft', 'posted', 'cancelled']
const PAYMENT_STATUSES: InvoicePaymentStatus[] = ['unpaid', 'partially_paid', 'paid', 'overdue']
const INVOICE_GROUPINGS: InvoiceGroupBy[] = [
  'customer',
  'posting_status',
  'payment_status',
  'month',
  'analysis_tag',
]

function stringArray(value: unknown): string[] | undefined {
  const values = Array.isArray(value) ? value : typeof value === 'string' ? [value] : []
  const strings = values.filter((item): item is string => typeof item === 'string' && item !== '')
  return strings.length ? strings : undefined
}

function isInvoiceGroupBy(value: unknown): value is InvoiceGroupBy {
  return typeof value === 'string' && INVOICE_GROUPINGS.includes(value as InvoiceGroupBy)
}

function isInvoicePostingStatus(value: unknown): value is InvoicePostingStatus {
  return typeof value === 'string' && (POSTING_STATUSES as string[]).includes(value)
}

function isInvoicePaymentStatus(value: unknown): value is InvoicePaymentStatus {
  return typeof value === 'string' && (PAYMENT_STATUSES as string[]).includes(value)
}

const INVOICE_SORTS: InvoiceSort[] = [
  'invoice_date',
  'document_number',
  'customer',
  'subtotal',
  'tax',
  'total',
  'amount_due',
  'due_date',
  'posting_status',
  'payment_status',
]
const INVOICE_STATUSES: InvoiceStatus[] = [
  'draft',
  'confirmed',
  'partially_paid',
  'paid',
  'cancelled',
]

function isInvoiceSort(value: unknown): value is InvoiceSort {
  return typeof value === 'string' && INVOICE_SORTS.includes(value as InvoiceSort)
}

function positiveInteger(value: unknown): number | undefined {
  const parsed = typeof value === 'string' && /^\d+$/.test(value) ? Number(value) : value
  return typeof parsed === 'number' && Number.isInteger(parsed) && parsed > 0 ? parsed : undefined
}

function invoicePageSize(value: unknown): number | undefined {
  const parsed = positiveInteger(value)
  return parsed === 10 || parsed === 25 || parsed === 50 || parsed === 100 ? parsed : undefined
}

function isInvoiceStatus(value: unknown): value is InvoiceStatus {
  return typeof value === 'string' && INVOICE_STATUSES.includes(value as InvoiceStatus)
}
